import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText, verifyCrossLinks } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  useVibesites,
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import {
  GalleryFadeCarousel,
  pickGallerySlides,
  type GalleryImage,
} from './_galleryShared';

/** Word count above which the blurb is clamped with a Read More toggle. */
const BLURB_CLAMP_WORDS = 1000;

const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;
const HTML_TAG_RE = /<[^>]+>/g;

/**
 * Drop markdown bold markers (`**…**`) that sometimes leak through from
 * property long-description copy. Keeps the words; removes the asterisks.
 */
function stripMarkdownBoldMarkers(html: string): string {
  return html.replace(/\*\*/g, '');
}

/** Plain-text word count from richtext HTML (tags ignored). */
function countWords(html: string): number {
  const text = html.replace(HTML_TAG_RE, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 0;
  return text.split(' ').length;
}

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/photos' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const apartmentPhotos = useCollectionBinding<GalleryImage>(
    'propertyoverview.apartment_photos',
    {
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
    },
  );
  const slides = useMemo(
    () => pickGallerySlides(apartmentPhotos, 5),
    [apartmentPhotos],
  );

  const secondaryCta = useSlotGroup('propertyoverview.cta_secondary', secondaryCtaDef);
  const { property, pagePaths } = useVibesites();
  const rawBlurb = useContentToken('propertyoverview.blurb');

  // Resolve + strip markdown bold markers so leaked `**` from CMS copy never
  // shows. Mirrors RichTextContent's resolve/verify/collapse pipeline, with
  // the extra strip step RichTextContent doesn't offer.
  const blurbHtml = useMemo(() => {
    if (!rawBlurb) return '';
    const resolved = resolveRichText(
      String(rawBlurb),
      property as Record<string, unknown>,
    );
    const verified = verifyCrossLinks(resolved, pagePaths);
    return stripMarkdownBoldMarkers(verified.replace(BLANK_P_RE, ''));
  }, [rawBlurb, property, pagePaths]);

  const [blurbExpanded, setBlurbExpanded] = useState(false);
  const isLongBlurb = countWords(blurbHtml) > BLURB_CLAMP_WORDS;

  useRevealStyles();
  useSiteCtaStyles();
  const blurbRef = useScrollReveal<HTMLDivElement>();
  const mediaRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="po-section">
      <div className="po-layout">
        <div className="po-text-column reveal-fade-up">
          <div ref={blurbRef} className="po-text-inner">
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="po-title font-heading"
            />

            <div
              className={`po-blurb-wrap ${
                blurbExpanded || !isLongBlurb ? 'po-blurb-wrap--open' : ''
              }`}
            >
              <EditableToken
                path="propertyoverview.blurb"
                type="richtext"
                as="div"
                className="po-blurb font-body"
              >
                <span
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: blurbHtml }}
                  style={{ display: 'contents' }}
                />
              </EditableToken>
            </div>
            {isLongBlurb && (
              <button
                type="button"
                onClick={() => setBlurbExpanded((v) => !v)}
                aria-expanded={blurbExpanded}
                className="po-read-more font-body"
              >
                {blurbExpanded ? (
                  <RichTextContent path="propertyoverview.read_less_label" as="span" />
                ) : (
                  <RichTextContent path="propertyoverview.read_more_label" as="span" />
                )}
              </button>
            )}

            <div className="po-cta-row">
              <EditableSlotGroup groupPath="propertyoverview.cta_secondary" definition={secondaryCtaDef}>
                <a
                  href={secondaryCta.url || '/photos'}
                  target={secondaryCta._target || '_self'}
                  className="site-cta site-cta--secondary"
                >
                  {secondaryCta.label || 'View Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        <div ref={mediaRef} className="po-media-column reveal-fade-up">
          <EditableCollectionBinding
            bindingPath="propertyoverview.apartment_photos"
            label="Apartment Photos"
            className="po-media"
          >
            <GalleryFadeCarousel
              images={slides}
              className="po-media-carousel"
              imgClassName="po-media-img"
              emptyClassName="po-media-img po-media-img--empty"
              sizes="(min-width: 992px) 46vw, 100vw"
            />
          </EditableCollectionBinding>
        </div>
      </div>

      <style>{`
        .po-section {
          position: relative;
          isolation: isolate;
          background: var(--vibe-color-background);
        }

        .po-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          max-width: 84rem;
          margin: 0 auto;
          padding: 3.5rem 1.5rem;
          align-items: center;
        }

        .po-text-inner {
          max-width: 40rem;
        }

        .po-title {
          margin: 0 0 1.75rem;
          /* Standardized Home section-title size (see FloorplansList,
             AmenitiesForYou, NeighborhoodSection, ResidentReview,
             SocialFeedSection). Kept at font-weight 700 to match. */
          font-size: clamp(2.25rem, 4vw, 3rem);
          line-height: 1.05;
          font-weight: 700;
          color: var(--vibe-color-primary);
          text-transform: none;
        }

        .po-title p,
        .po-title span {
          margin: 0;
          color: inherit;
          font: inherit;
        }

        .po-blurb-wrap {
          max-height: 9.6em;
          overflow: hidden;
          margin-bottom: 2rem;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }

        .po-blurb-wrap:has(+ .po-read-more) {
          margin-bottom: 0;
        }

        .po-blurb-wrap--open {
          max-height: none;
          -webkit-mask-image: none;
          mask-image: none;
        }

        .po-blurb {
          margin: 0;
          font-size: 1.0625rem;
          line-height: 1.7;
          color: var(--vibe-color-text);
        }

        .po-blurb p {
          margin: 0 0 1.15rem;
        }

        .po-blurb p:last-child {
          margin-bottom: 0;
        }

        .po-read-more {
          display: inline-block;
          margin: 0.75rem 0 2rem;
          padding: 0;
          border: 0;
          background: transparent;
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: var(--vibe-color-primary);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .po-read-more:hover {
          text-decoration: underline;
        }

        .po-read-more p,
        .po-read-more span {
          margin: 0;
          color: inherit;
          font: inherit;
        }

        .po-cta-row {
          display: grid;
          grid-auto-flow: row;
          grid-template-columns: max-content;
          gap: 1rem;

          /* Scoped override for this section's secondary CTA:
             Base state fills with the theme primary color; hover swaps
             to the surface token. Hover ink is luminance-based so light
             or dark surface tokens both stay readable. */
          --site-cta-secondary-accent: var(--vibe-color-primary);
          --site-cta-secondary-on-accent: var(--site-cta-ink-on-primary, var(--vibe-color-text-on-primary, #ffffff));
          --site-cta-hover-accent: var(--vibe-color-surface);
          --site-cta-hover-on-accent: var(--site-cta-ink-on-surface, #ffffff);
        }

        .po-cta-row .site-cta {
          width: 100%;
        }

        .po-media-column {
          min-width: 0;
        }

        .po-media {
          position: relative;
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          border-radius: 15px;
          box-shadow: 0 22px 55px rgba(0, 0, 0, 0.18);
        }

        .po-media-carousel {
          position: absolute;
          inset: 0;
        }

        .po-media-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .po-media-img--empty {
          background: var(--vibe-color-secondary);
        }

        /* Mobile: center the copy column and CTA stack. Desktop keeps the
           left-aligned two-column layout via the min-width rules below. */
        @media (max-width: 767.98px) {
          .po-text-column {
            text-align: center;
          }
          .po-text-inner {
            margin-left: auto;
            margin-right: auto;
          }
          .po-cta-row {
            justify-content: center;
          }
        }

        @media (min-width: 768px) {
          .po-layout {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem;
            padding: 5rem 2rem;
          }
        }

        @media (min-width: 992px) {
          .po-layout {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 4rem;
            padding: 6rem 2.5rem;
          }

          .po-media {
            aspect-ratio: 4 / 3.15;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_romance_title',
      defaultValue: '<p>For the next generation of innovators</p>',
    },
    'propertyoverview.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        '<p>Discover the perfect balance of academic focus and social energy. Designed with students in mind, our fully furnished floor plans offer high-speed internet, in-unit laundry, and modern kitchens — everything you need to stay connected and comfortable.</p><p>Ready to elevate your college experience? Explore our floor plans and check out layouts designed to fit your lifestyle.</p>',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_secondary': secondaryCtaDef,
  },
  collectionBindings: {
    'propertyoverview.apartment_photos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: 5,
      description:
        'Home Property Overview carousel. Filters Photo Gallery title=Apartment; prefers description=Featured when any exist, otherwise up to 5 apartment photos.',
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
